import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { PageHeader } from '../../components/common/PageHeader';
import { Modal } from '../../components/common/Modal';
import { MerchandiseItem, MerchandiseOrder, InvoiceItem, Gelanggang, Role, UserProfile } from '../../types';
import { saveInvoiceDB, getMembersDB } from '../../services/db';
import { useMerchandiseData } from '../../hooks/useMerchandiseData';
import { saveViewScroll, restoreViewScroll, resetScrollToTop } from '../../utils/scrollRestoration';
import {
  ShoppingBag,
  Tag,
  ClipboardList,
  Package,
  Plus,
  Check,
  X,
  CheckCircle,
  AlertTriangle,
  ShieldAlert,
  ExternalLink,
  Search,
  ShoppingCart,
  QrCode,
  MessageSquare,
  ArrowLeft,
  ChevronRight,
  Edit3,
  Info,
  Ruler,
  Lock,
  Shield
} from 'lucide-react';
import { isSuperAdmin, isUserManagement, formatSellerName, isFamilyMember } from '../../utils/formatters';
import { DuitNowQrModal } from '../../components/merchandise/DuitNowQrModal';
import { getUniformPriceBySize } from '../../utils/pricing';
import { UniformSizeChartModal } from '../../components/common/UniformSizeChartModal';
import { ASMM_BENGKUNG_RANKS, toDbBengkungCode } from '../../data/bengkungRanks';

// Bengkung rank tiers: Hitam (0) -> Putih (1) -> Merah (2) -> Kuning (3) -> Biru (4) -> Guru Utama (5)
function getBengkungColorTier(text?: string): number {
  if (!text) return 0;
  const lower = text.toLowerCase();
  if (lower.includes('guru utama')) return 5;
  if (lower.includes('biru')) return 4;
  if (lower.includes('kuning')) return 3;
  if (lower.includes('merah')) return 2;
  if (lower.includes('putih')) return 1;
  if (lower.includes('hitam')) return 0;
  return 0;
}

const BENGKUNG_TIER_NAMES = [
  'Bengkung Hitam',
  'Bengkung Putih',
  'Bengkung Merah',
  'Bengkung Kuning',
  'Bengkung Biru',
  'Bengkung Guru Utama'
];

// Resolves rank index in official ASMM_BENGKUNG_RANKS
function getRankIndex(bengkungStr?: string): number {
  if (!bengkungStr) return 0;
  const dbCode = toDbBengkungCode(bengkungStr);
  const idx = ASMM_BENGKUNG_RANKS.findIndex(r => r.dbCode === dbCode);
  if (idx !== -1) return idx;
  const byCode = ASMM_BENGKUNG_RANKS.findIndex(r => r.code.toLowerCase() === bengkungStr.toLowerCase());
  return byCode !== -1 ? byCode : 0;
}

// Extracts numeric cula count from variation label (e.g. 'Cula 2' -> 2, 'Kosong' -> 0)
function getCulaNumber(variationStr?: string): number {
  if (!variationStr) return 0;
  if (variationStr.toLowerCase().includes('kosong')) return 0;
  const match = variationStr.match(/\d+/);
  return match ? parseInt(match[0], 10) : 0;
}

// Maximum allowed cula count per color according to ASMM syllabus hierarchy
function getMaxCulaForColor(colorStr?: string): number {
  if (!colorStr) return 0;
  const lower = colorStr.toLowerCase();
  if (lower.includes('kuning')) return 7;
  if (lower.includes('merah')) return 3;
  if (lower.includes('putih')) return 3;
  if (lower.includes('hitam')) return 3;
  return 0;
}

// Validates whether a specific cula variation is unlocked for the member and family
function isBengkungVariationLocked(
  variationStr: string,
  colorStr: string,
  user: UserProfile | null,
  maxFamilyRankIndex: number
): { isLocked: boolean; reason?: string } {
  if (!user) {
    return { isLocked: true, reason: 'Sila log masuk untuk menempah bengkung mengikut kelayakan.' };
  }

  const culaCount = getCulaNumber(variationStr);
  const maxCula = getMaxCulaForColor(colorStr);

  // Validate if this cula number exists in the belt's curriculum
  if (culaCount > maxCula) {
    return {
      isLocked: true,
      reason: `Bengkung ${colorStr} hanya mempunyai sehingga ${maxCula === 0 ? 'tiada cula' : `${maxCula} Cula`} dalam silibus rasmi persatuan.`
    };
  }

  // Construct target rank name e.g. "Putih" or "Putih 2"
  const targetCode = culaCount === 0 ? colorStr : `${colorStr} ${culaCount}`;
  const targetDbCode = toDbBengkungCode(targetCode);
  const targetIndex = ASMM_BENGKUNG_RANKS.findIndex(r => r.dbCode === targetDbCode);
  const targetRank = targetIndex !== -1 ? ASMM_BENGKUNG_RANKS[targetIndex] : null;

  if (targetIndex === -1 || targetIndex > maxFamilyRankIndex) {
    return {
      isLocked: true,
      reason: `Peringkat ${targetCode} (${targetRank?.rankTitle || 'Cula Terkunci'}) belum dicapai oleh anda atau ahli keluarga berdaftar.`
    };
  }

  return { isLocked: false };
}

interface MerchandisePageProps {
  onNavigateTab?: (tab: string) => void;
}

export const MerchandisePage: React.FC<MerchandisePageProps> = ({ onNavigateTab }) => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const { items, isLoading, saveOrder } = useMerchandiseData();

  const isManagement = Boolean(currentUser && isUserManagement(currentUser));

  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [placedOrderForQr, setPlacedOrderForQr] = useState<MerchandiseOrder | null>(null);

  const [members, setMembers] = useState<UserProfile[]>([]);
  useEffect(() => {
    getMembersDB().then(setMembers).catch(() => { });
  }, []);

  // Routing State for Single Product View
  const [selectedProductId, setSelectedProductId] = useState<string | null>(() => {
    const parts = window.location.pathname.split('/');
    if (parts.length > 2 && parts[1] === 'merchandise') {
      return parts[2];
    }
    return null;
  });

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      const parts = window.location.pathname.split('/');
      if (parts.length > 2 && parts[1] === 'merchandise') {
        setSelectedProductId(parts[2]);
      } else {
        setSelectedProductId(null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigateToProduct = (productId: string | null) => {
    if (productId) {
      saveViewScroll('merchandise_catalog');
      setSelectedProductId(productId);
      const targetPath = `/merchandise/${productId}`;
      if (window.location.pathname !== targetPath) {
        window.history.pushState(null, '', targetPath);
      }
      resetScrollToTop();
    } else {
      setSelectedProductId(null);
      if (window.location.pathname !== '/merchandise') {
        window.history.pushState(null, '', '/merchandise');
      }
      restoreViewScroll('merchandise_catalog');
    }
  };

  const selectedProduct = useMemo(() => {
    return items.find(item => item.id === selectedProductId);
  }, [items, selectedProductId]);

  const seller = useMemo(() => {
    if (!selectedProduct) return null;
    const sellerId = selectedProduct.userId || 'ASMM-011';
    return members.find(m => m.id.toLowerCase() === sellerId.toLowerCase());
  }, [members, selectedProduct]);

  // Booking Form State for Single Product Detail
  const [quantity, setQuantity] = useState<number>(1);
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [selectedVariation, setSelectedVariation] = useState<string>('');
  const [deliveryMethod, setDeliveryMethod] = useState<'Ambil di Gelanggang' | 'Penghantaran Pos'>('Ambil di Gelanggang');
  const [notes, setNotes] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSizeChartOpen, setIsSizeChartOpen] = useState<boolean>(false);

  const isBajuSilat = Boolean(
    selectedProduct &&
    (selectedProduct.id === 'merch-baju-silat' || selectedProduct.name.toLowerCase().includes('baju silat'))
  );

  const isBengkung = Boolean(
    selectedProduct &&
    (selectedProduct.id.includes('bengkung') ||
      selectedProduct.name.toLowerCase().includes('bengkung') ||
      selectedProduct.category.toLowerCase().includes('kelengkapan'))
  );

  // Immediate family unit of current member (children, spouse, dependents)
  const familyMembers = useMemo(() => {
    if (!currentUser) return [];
    return members.filter(m => isFamilyMember(m, currentUser));
  }, [currentUser, members]);

  // Maximum bengkung level among member and all their registered family members
  const maxFamilyBengkung = useMemo(() => {
    if (!currentUser) {
      return { maxTier: 0, maxRankIndex: 0, highestRankTitle: 'Tiada', maxColorName: 'Bengkung Hitam' };
    }
    const familyUnit = [currentUser, ...familyMembers];
    let maxTier = 0;
    let maxRankIndex = 0;
    let highestRankTitle = currentUser.bengkung || 'Bengkung Putih';

    familyUnit.forEach(member => {
      const tier = getBengkungColorTier(member.bengkung);
      const rIdx = getRankIndex(member.bengkung);
      if (tier > maxTier) {
        maxTier = tier;
      }
      if (rIdx >= maxRankIndex) {
        maxRankIndex = rIdx;
        const rankInfo = ASMM_BENGKUNG_RANKS[rIdx];
        highestRankTitle = rankInfo ? `${rankInfo.code} (${rankInfo.rankTitle})` : (member.bengkung || highestRankTitle);
      }
    });

    const maxColorName = BENGKUNG_TIER_NAMES[maxTier] || 'Bengkung Putih';
    return { maxTier, maxRankIndex, highestRankTitle, maxColorName };
  }, [currentUser, familyMembers]);

  // Available sizes: for Baju Silat, always match official registration sizing 110cm - 210cm
  const productSizes = useMemo(() => {
    if (!selectedProduct) return [];
    if (isBajuSilat) {
      return ['110cm', '120cm', '130cm', '140cm', '150cm', '160cm', '170cm', '180cm', '190cm', '200cm', '210cm'];
    }
    return selectedProduct.sizes || [];
  }, [selectedProduct, isBajuSilat]);

  // Dynamic unit price based on size (if Baju Silat, follows daftar process formula)
  const currentUnitPrice = useMemo(() => {
    if (!selectedProduct) return 0;
    if (isBajuSilat) {
      return getUniformPriceBySize(selectedSize || '150cm');
    }
    return selectedProduct.price;
  }, [selectedProduct, isBajuSilat, selectedSize]);

  // Initialize booking details when product page loads
  useEffect(() => {
    if (selectedProduct) {
      setQuantity(1);
      let defaultSize = '';
      if (isBajuSilat) {
        defaultSize = '150cm';
      } else if (isBengkung && selectedProduct.sizes && selectedProduct.sizes.length > 0) {
        // Select highest allowed bengkung color for user & family
        const allowedOption = selectedProduct.sizes.find(s => {
          return getBengkungColorTier(s) <= maxFamilyBengkung.maxTier;
        });
        defaultSize = allowedOption || selectedProduct.sizes[0];
      } else if (selectedProduct.sizes && selectedProduct.sizes.length > 0) {
        defaultSize = selectedProduct.sizes[0];
      }
      setSelectedSize(defaultSize);

      let defaultVariation = '';
      if (isBengkung && selectedProduct.variations && selectedProduct.variations.length > 0) {
        const allowedVar = selectedProduct.variations.find(v => {
          return !isBengkungVariationLocked(v, defaultSize || 'Hitam', currentUser, maxFamilyBengkung.maxRankIndex).isLocked;
        });
        defaultVariation = allowedVar || selectedProduct.variations[0];
      } else if (selectedProduct.variations && selectedProduct.variations.length > 0) {
        defaultVariation = selectedProduct.variations[0];
      }
      setSelectedVariation(defaultVariation);

      setDeliveryMethod('Ambil di Gelanggang');
      setNotes('');
    }
  }, [selectedProduct, isBajuSilat, isBengkung, maxFamilyBengkung.maxTier, maxFamilyBengkung.maxRankIndex, currentUser]);

  // Keep variation synchronized and valid when bengkung color selection changes
  useEffect(() => {
    if (isBengkung && selectedProduct?.variations && selectedProduct.variations.length > 0 && selectedSize) {
      const lockStatus = isBengkungVariationLocked(
        selectedVariation,
        selectedSize,
        currentUser,
        maxFamilyBengkung.maxRankIndex
      );
      if (!selectedVariation || lockStatus.isLocked) {
        const allowedVar = selectedProduct.variations.find(v => {
          return !isBengkungVariationLocked(v, selectedSize, currentUser, maxFamilyBengkung.maxRankIndex).isLocked;
        });
        if (allowedVar) {
          setSelectedVariation(allowedVar);
        }
      }
    }
  }, [selectedSize, isBengkung, maxFamilyBengkung.maxRankIndex, currentUser, selectedProduct]);

  // Filter catalog items
  const filteredCatalog = useMemo(() => {
    return items.filter(item => {
      const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
      const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [items, categoryFilter, searchTerm]);

  // Place order flow (Shopee flow)
  const handlePlaceOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !selectedProduct) {
      showToast('error', 'Log Masuk Diperlukan', 'Sila log masuk ke portal persatuan terlebih dahulu untuk menempah barangan.');
      return;
    }

    if (isBengkung) {
      if (selectedSize) {
        const optionTier = getBengkungColorTier(selectedSize);
        if (optionTier > maxFamilyBengkung.maxTier) {
          showToast(
            'error',
            'Peringkat Bengkung Belum Layak',
            `Anda dan ahli keluarga anda belum mencapai tahap Bengkung ${selectedSize}. Pembelian bengkung dihadkan mengikut kelayakan tertinggi (sehingga ${maxFamilyBengkung.maxColorName}).`
          );
          return;
        }
      }

      if (selectedVariation) {
        const culaLock = isBengkungVariationLocked(
          selectedVariation,
          selectedSize || 'Hitam',
          currentUser,
          maxFamilyBengkung.maxRankIndex
        );
        if (culaLock.isLocked) {
          showToast(
            'error',
            'Peringkat Cula Belum Layak',
            culaLock.reason || `Peringkat ${selectedVariation} belum dicapai oleh anda atau ahli keluarga berdaftar.`
          );
          return;
        }
      }
    }

    setIsSubmitting(true);
    const orderId = `order-${Date.now()}`;
    const invoiceId = `inv-merch-${Date.now().toString().slice(-4)}`;
    const shippingFee = deliveryMethod === 'Penghantaran Pos' ? 8.0 : 0.0;
    const unitPrice = currentUnitPrice;
    const totalAmount = unitPrice * quantity + shippingFee;

    const fullItemName = selectedProduct.name + (selectedVariation ? ` (${selectedVariation})` : '');

    const newOrder: MerchandiseOrder = {
      id: orderId,
      memberId: currentUser.id,
      memberName: currentUser.name,
      phone: currentUser.phone,
      gelanggang: currentUser.gelanggang,
      itemId: selectedProduct.id,
      itemName: fullItemName,
      itemCategory: selectedProduct.category,
      quantity,
      size: selectedSize || undefined,
      totalAmount,
      deliveryMethod,
      notes: notes || undefined,
      status: 'Menunggu Kelulusan',
      invoiceId,
      createdAt: new Date().toISOString().split('T')[0],
      paymentMethod: 'DuitNow QR',
      paymentStatus: 'Belum Dibayar'
    };

    // Auto-generate unpaid invoice so user can pay via Bayaran tab
    const newInvoice: InvoiceItem = {
      id: invoiceId,
      invoiceNo: `INV-${Date.now().toString().slice(-6)}`,
      memberId: currentUser.id,
      memberName: currentUser.name,
      memberIcNo: currentUser.icNo,
      memberPhone: currentUser.phone,
      gelanggang: currentUser.gelanggang,
      category: 'Lain-lain',
      title: `Merchandise: ${selectedProduct.name} (x${quantity})`,
      description: `Tempahan barangan merchandise ASMM. Kuantiti: ${quantity}${selectedSize ? `, ${isBengkung ? 'Warna' : 'Saiz'}: ${selectedSize} (RM${unitPrice.toFixed(2)})` : ''}${selectedVariation ? `, Variasi: ${selectedVariation}` : ''}. Kaedah Penghantaran: ${deliveryMethod}.`,
      amount: totalAmount,
      dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      createdAt: new Date().toISOString().split('T')[0],
      status: 'Belum Dibayar'
    };

    try {
      // 1. Save invoice to database
      await saveInvoiceDB(newInvoice);
      // 2. Save order to database
      await saveOrder(newOrder);

      showToast(
        'success',
        'Tempahan Berjaya Direkodkan!',
        `Sila imbas kod QR DuitNow untuk menyempurnakan pembayaran RM${totalAmount.toFixed(2)}.`
      );

      // Open instant QR payment modal for the buyer
      setPlacedOrderForQr(newOrder);
    } catch (err) {
      showToast('error', 'Ralat Penghantaran', 'Gagal memproses tempahan anda. Sila cuba lagi.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentSubmitted = async (updatedOrder: MerchandiseOrder) => {
    await saveOrder(updatedOrder);
    setPlacedOrderForQr(null);
    navigateToProduct(null);
    if (onNavigateTab) {
      onNavigateTab('pembelian');
    } else {
      window.history.pushState(null, '', '/pembelian');
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  };

  const handleQrModalClose = () => {
    setPlacedOrderForQr(null);
    navigateToProduct(null);
    if (onNavigateTab) {
      onNavigateTab('pembelian');
    } else {
      window.history.pushState(null, '', '/pembelian');
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  };

  return (
    <div className="w-full space-y-6 min-h-[75vh]">
      {/* Page Header */}
      <PageHeader
        badgeText="ASMM Merchs"
        badgeIcon={ShoppingBag}
        title="Kedai"
      >
        {isManagement && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (onNavigateTab) {
                  onNavigateTab('inventory');
                } else {
                  window.history.pushState(null, '', '/inventory');
                  window.dispatchEvent(new PopStateEvent('popstate'));
                }
              }}
              className="px-4 py-2 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold text-xs rounded-2xl transition-all shadow-md flex items-center gap-2 cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              <span>Urus & Tambah Produk</span>
            </button>
          </div>
        )}
      </PageHeader>

      {/* RENDER TAB 1: PRODUCT SINGLE DETAIL VIEW */}
      {selectedProduct ? (
        <div className="space-y-6">
          {/* Breadcrumb / Back button */}
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => navigateToProduct(null)}
              className="p-1.5 sm:p-2 hover:bg-slate-100 rounded-full transition-colors cursor-pointer text-slate-600 hover:text-slate-900 shrink-0"
              title="Kembali ke Katalog"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            <button
              type="button"
              onClick={() => {
                setCategoryFilter('all');
                navigateToProduct(null);
              }}
              className="text-xs text-slate-500 hover:text-asmm-red font-bold transition-colors cursor-pointer hover:underline"
              title="Kembali ke semua barangan katalog"
            >
              Katalog
            </button>
            <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <button
              type="button"
              onClick={() => {
                setCategoryFilter(selectedProduct.category);
                navigateToProduct(null);
              }}
              className="text-xs text-slate-500 hover:text-asmm-red font-bold transition-colors cursor-pointer hover:underline"
              title={`Tapis barangan mengikut kategori ${selectedProduct.category}`}
            >
              {selectedProduct.category}
            </button>
            <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="text-xs text-slate-800 font-extrabold truncate max-w-[200px] sm:max-w-xs" aria-current="page">
              {selectedProduct.name}
            </span>
          </nav>

          {/* Product Detail Layout (Shopee flow structure) */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 bg-white border border-slate-200 rounded-3xl p-5 sm:p-6 shadow-xs">
            {/* Image Box */}
            <div className="lg:col-span-5 space-y-4">
              <div className="aspect-square bg-slate-50 border border-slate-200 rounded-2xl overflow-hidden shadow-inner">
                <img
                  src={selectedProduct.photoUrl}
                  alt={selectedProduct.name}
                  onError={(e) => { (e.target as HTMLImageElement).src = '/logo-pssasmm.png'; }}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Product Configuration & Buy Box */}
            <div className="lg:col-span-7 space-y-6">
              <div>
                <span className="text-[10px] font-extrabold px-2.5 py-0.5 rounded-full text-asmm-red bg-red-50 border border-red-100 uppercase tracking-wider">
                  {selectedProduct.category}
                </span>
                <h2 className="text-lg sm:text-2xl font-extrabold text-slate-900 mt-2">
                  {selectedProduct.name}
                </h2>
                <div className="flex items-center gap-3 mt-3 flex-wrap">
                  <div className="text-2xl font-extrabold text-asmm-red">
                    RM {currentUnitPrice.toFixed(2)}
                  </div>
                  <span className="text-slate-300">|</span>
                  <div className="flex items-center gap-1">
                    <span className={`w-2 h-2 rounded-full ${selectedProduct.stockStatus === 'Dalam Stok' ? 'bg-emerald-500' : selectedProduct.stockStatus === 'Stok Rendah' ? 'bg-amber-500' : 'bg-rose-500'
                      }`} />
                    <span className="text-xs font-bold text-slate-500">{selectedProduct.stockStatus}</span>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <h4 className="text-xs font-extrabold uppercase tracking-wider text-slate-400">Deskripsi Produk</h4>
                <p className="text-xs text-slate-600 leading-relaxed font-medium">
                  {selectedProduct.description}
                </p>
              </div>

              {/* Specifications / Detail Jualan (Shopee specs style) */}
              {(Boolean(selectedProduct.specifications && Object.keys(selectedProduct.specifications).length > 0) || isBajuSilat) && (
                <div className="space-y-2">
                  <h4 className="text-xs font-extrabold uppercase tracking-wider text-slate-400">Spesifikasi Produk</h4>
                  <div className="bg-slate-50 border border-slate-100 rounded-xl p-3 text-xs space-y-3">
                    {selectedProduct.specifications && Object.keys(selectedProduct.specifications).length > 0 && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {Object.entries(selectedProduct.specifications).map(([key, val]) => (
                          <div key={key} className="flex gap-2">
                            <span className="font-bold text-slate-400 w-24 shrink-0">{key}</span>
                            <span className="font-bold text-slate-700">{val}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Size Chart Row - Grouped at the most bottom of Spesifikasi Produk */}
                    {isBajuSilat && (
                      <div className="pt-2.5 border-t border-slate-200/80 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2.5">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-slate-400 w-24 shrink-0">Carta Saiz</span>
                          <span className="font-bold text-slate-700">110cm – 210cm</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setIsSizeChartOpen(true)}
                          className="inline-flex items-center gap-1.5 text-xs font-extrabold text-asmm-red hover:text-red-700 transition-colors cursor-pointer bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-xl border border-red-200 shadow-xs shrink-0"
                          title="Buka Carta Cadangan Saiz Uniform"
                        >
                          <Ruler className="w-3.5 h-3.5" />
                          <span>Buka Carta Cadangan Saiz</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Seller Shop Profile Section (Shopee layout flow) */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 bg-slate-50 border border-slate-200 rounded-2xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-asmm-red text-white flex items-center justify-center font-extrabold text-xs shadow-xs shrink-0 uppercase">
                    {seller?.name ? formatSellerName(seller.name).slice(0, 2) : 'AS'}
                  </div>
                  <div>
                    <h4 className="font-extrabold text-slate-800 text-[11px] sm:text-xs leading-none">{formatSellerName(seller?.name)}</h4>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-[10px] text-slate-500 font-bold">
                      <span className="text-asmm-red">{seller?.role || 'Pengurus Kedah'}</span>
                      <span className="text-slate-350">•</span>
                      <span>📍 {seller?.gelanggang === 'Semua Gelanggang' ? 'ASMM' : (seller?.gelanggang || 'ASMM')}</span>
                    </div>
                  </div>
                </div>
                {seller?.phone && (() => {
                  let cleanPhone = seller.phone.replace(/[^0-9]/g, '');
                  if (cleanPhone.startsWith('0')) {
                    cleanPhone = '6' + cleanPhone;
                  } else if (!cleanPhone.startsWith('60') && cleanPhone.startsWith('1')) {
                    cleanPhone = '60' + cleanPhone;
                  }
                  return (
                    <a
                      href={`https://wa.me/${cleanPhone}?text=${encodeURIComponent(
                        `Salam sejahtera, saya berminat untuk bertanya mengenai produk *${selectedProduct.name}* di portal ASMM:\n${window.location.origin}/merchandise/${selectedProduct.id}`
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-extrabold rounded-xl transition-all shadow-xs flex items-center gap-1.5 cursor-pointer no-underline shrink-0"
                    >
                      <MessageSquare className="w-3.5 h-3.5" />
                      Hubungi Whatsapp
                    </a>
                  );
                })()}
              </div>

              {/* Order Form */}
              <form onSubmit={handlePlaceOrder} className="space-y-4 pt-4 border-t border-slate-100 text-xs">
                {/* Product Options: Bengkung has Warna above Variasi, Baju Silat & other items have Variasi above Pilihan Saiz */}
                {(() => {
                  const sizeSelectionBlock = productSizes && productSizes.length > 0 && (
                    <div key="sizes-block">
                      <label className="block text-slate-700 font-bold mb-1.5">
                        {isBengkung
                          ? 'Pilihan Warna Bengkung *'
                          : `Pilihan Saiz ${isBajuSilat ? '(Mengikut Ketinggian)' : ''} *`}
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {productSizes.map(s => {
                          const isSelected = selectedSize === s;
                          const optionTier = isBengkung ? getBengkungColorTier(s) : 0;
                          const isLocked = isBengkung && (!currentUser || optionTier > maxFamilyBengkung.maxTier);

                          if (isLocked) {
                            return (
                              <button
                                key={s}
                                type="button"
                                onClick={() => {
                                  showToast(
                                    'error',
                                    'Peringkat Belum Layak',
                                    `Peringkat Bengkung ${s} memerlukan kenaikan taraf bengkung. Tahap tertinggi anda atau ahli keluarga berdaftar: ${maxFamilyBengkung.maxColorName}.`
                                  );
                                }}
                                className="px-3.5 py-1.5 rounded-xl border border-dashed border-slate-300 bg-slate-100 text-slate-400 text-xs font-bold transition-all cursor-not-allowed flex items-center gap-1.5 opacity-60 hover:opacity-80"
                                title={`Terkunci: Memerlukan kelayakan Bengkung ${s}`}
                              >
                                <Lock className="w-3 h-3 text-slate-400" />
                                <span className="line-through">{s}</span>
                                <span className="text-[10px] font-normal text-slate-400 no-underline">(Terkunci)</span>
                              </button>
                            );
                          }

                          return (
                            <button
                              key={s}
                              type="button"
                              onClick={() => setSelectedSize(s)}
                              className={`px-3.5 py-1.5 rounded-xl border text-xs font-bold transition-all cursor-pointer ${isSelected
                                ? 'bg-slate-900 text-white border-slate-900 shadow-xs ring-2 ring-slate-300'
                                : 'bg-white text-slate-600 hover:bg-slate-50 border-slate-200 hover:border-slate-300'
                                }`}
                            >
                              {s}
                            </button>
                          );
                        })}
                      </div>

                      {/* Bengkung Eligibility Trust Banner */}
                      {isBengkung && currentUser && (
                        <div className="mt-2 text-xs flex items-center gap-2 p-2.5 bg-slate-50 border border-slate-200/80 rounded-xl text-slate-600">
                          <Shield className="w-4 h-4 text-asmm-red shrink-0" />
                          <span>
                            Kelayakan tertinggi anda & keluarga: <strong className="text-slate-800">{maxFamilyBengkung.highestRankTitle}</strong> (Dihadkan mengikut tahap warna & cula yang dicapai).
                          </span>
                        </div>
                      )}

                      {isBengkung && !currentUser && (
                        <div className="mt-2 text-xs flex items-center gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-xl text-amber-800">
                          <Lock className="w-4 h-4 text-amber-600 shrink-0" />
                          <span>
                            Sila log masuk untuk membeli bengkung mengikut peringkat kelayakan anda atau keluarga berdaftar.
                          </span>
                        </div>
                      )}
                    </div>
                  );

                  const variationsSelectionBlock = selectedProduct.variations && selectedProduct.variations.length > 0 && (
                    <div key="variations-block">
                      <label className="block text-slate-700 font-bold mb-1.5">
                        {isBengkung ? 'Pilihan Cula / Variasi *' : 'Pilihan Variasi *'}
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {selectedProduct.variations.map(v => {
                          const isSelected = selectedVariation === v;
                          const culaLock = isBengkung
                            ? isBengkungVariationLocked(v, selectedSize || 'Hitam', currentUser, maxFamilyBengkung.maxRankIndex)
                            : { isLocked: false };

                          if (culaLock.isLocked) {
                            return (
                              <button
                                key={v}
                                type="button"
                                onClick={() => {
                                  showToast(
                                    'error',
                                    'Peringkat Cula Belum Layak',
                                    culaLock.reason || `Peringkat ${v} memerlukan kenaikan taraf cula. Tahap tertinggi anda atau ahli keluarga berdaftar: ${maxFamilyBengkung.highestRankTitle}.`
                                  );
                                }}
                                className="px-3 py-1.5 rounded-xl border border-dashed border-slate-300 bg-slate-100 text-slate-400 text-xs font-bold transition-all cursor-not-allowed flex items-center gap-1.5 opacity-60 hover:opacity-80"
                                title={`Terkunci: ${culaLock.reason}`}
                              >
                                <Lock className="w-3 h-3 text-slate-400" />
                                <span className="line-through">{v}</span>
                                <span className="text-[10px] font-normal text-slate-400 no-underline">(Terkunci)</span>
                              </button>
                            );
                          }

                          return (
                            <button
                              key={v}
                              type="button"
                              onClick={() => setSelectedVariation(v)}
                              className={`px-3 py-1.5 rounded-xl border text-xs font-bold transition-all cursor-pointer ${isSelected
                                ? 'bg-slate-900 text-white border-slate-900 shadow-xs ring-2 ring-slate-300'
                                : 'bg-white text-slate-600 hover:bg-slate-50 border-slate-200 hover:border-slate-300'
                                }`}
                            >
                              {v}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );

                  return isBengkung ? (
                    <>
                      {sizeSelectionBlock}
                      {variationsSelectionBlock}
                    </>
                  ) : (
                    <>
                      {variationsSelectionBlock}
                      {sizeSelectionBlock}
                    </>
                  );
                })()}

                {/* Quantity & Delivery Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Quantity */}
                  <div>
                    <label className="block text-slate-700 font-bold mb-1">Kuantiti Tempahan *</label>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setQuantity(q => Math.max(1, q - 1))}
                        className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-250 flex items-center justify-center font-extrabold text-slate-700 cursor-pointer"
                      >
                        -
                      </button>
                      <input
                        type="number"
                        min={1}
                        max={99}
                        value={quantity}
                        onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))}
                        className="w-12 h-8 text-center bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red"
                      />
                      <button
                        type="button"
                        onClick={() => setQuantity(q => q + 1)}
                        className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-250 flex items-center justify-center font-extrabold text-slate-700 cursor-pointer"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  {/* Delivery Mode */}
                  <div>
                    <label className="block text-slate-700 font-bold mb-1">Kaedah Penghantaran *</label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setDeliveryMethod('Ambil di Gelanggang')}
                        className={`px-3 py-1.5 rounded-lg border font-bold transition-all cursor-pointer ${deliveryMethod === 'Ambil di Gelanggang'
                          ? 'bg-asmm-red text-white border-asmm-red'
                          : 'bg-white text-slate-600 hover:bg-slate-50 border-slate-200'
                          }`}
                      >
                        Pickup
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeliveryMethod('Penghantaran Pos')}
                        className={`px-3 py-1.5 rounded-lg border font-bold transition-all cursor-pointer ${deliveryMethod === 'Penghantaran Pos'
                          ? 'bg-asmm-red text-white border-asmm-red'
                          : 'bg-white text-slate-600 hover:bg-slate-50 border-slate-200'
                          }`}
                      >
                        Pos (+RM8.00)
                      </button>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-slate-700 font-bold mb-1">Nota Tambahan</label>
                  <textarea
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Contoh: Saiz khas, arahan pembungkusan, warna lain."
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-asmm-red"
                  />
                </div>

                {/* Submit Action */}
                <div className="flex items-center gap-3 pt-3 border-t border-slate-100">
                  <button
                    type="submit"
                    disabled={isSubmitting || selectedProduct.stockStatus === 'Tiada Stok'}
                    className="flex-1 py-3 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold rounded-xl transition-all shadow-md flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 active:scale-95"
                  >
                    <QrCode className="w-4 h-4" />
                    <span>{isSubmitting ? 'Memproses...' : 'Sahkan & Bayar Kod QR'}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : (
        /* CATALOG GRID (NORMAL VIEW) */
        <div className="space-y-6">
          {/* Controls Filter Bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-xs">
            {/* Search */}
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3.5 top-2.5 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Cari barangan kedai..."
                className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:border-asmm-red focus:bg-white focus:outline-none text-xs font-bold text-slate-900"
              />
            </div>

            {/* Category Filter Tabs */}
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar flex-nowrap w-full sm:w-auto pb-0.5">
              <span className="text-xs text-slate-400 font-bold mr-1 whitespace-nowrap shrink-0">Kategori:</span>
              {[
                { id: 'all', label: 'Semua' },
                { id: 'Pakaian', label: 'Pakaian' },
                { id: 'Kelengkapan', label: 'Kelengkapan Silat' },
                { id: 'Aksesori', label: 'Aksesori' }
              ].map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setCategoryFilter(cat.id)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap shrink-0 border transition-all cursor-pointer ${categoryFilter === cat.id
                    ? 'bg-asmm-red text-white border-asmm-red shadow-xs'
                    : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border-slate-200'
                    }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Grid list of catalog */}
          {isLoading ? (
            <div className="bg-white border border-slate-200 rounded-3xl p-12 text-center">
              <div className="w-8 h-8 border-3 border-asmm-red border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-xs text-slate-400 font-medium mt-3">Memuatkan katalog barangan...</p>
            </div>
          ) : filteredCatalog.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCatalog.map(item => (
                <div
                  key={item.id}
                  onClick={() => navigateToProduct(item.id)}
                  className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-xs hover:shadow-md hover:border-slate-300 transition-all flex flex-col justify-between group cursor-pointer"
                >
                  <div>
                    {/* Item Image */}
                    <div className="relative aspect-square bg-slate-100 overflow-hidden border-b border-slate-200">
                      <img
                        src={item.photoUrl}
                        alt={item.name}
                        onError={(e) => { (e.target as HTMLImageElement).src = '/logo-pssasmm.png'; }}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <span className="absolute top-3 left-3 px-2.5 py-1 bg-slate-900/90 text-white text-[9px] font-extrabold rounded-full tracking-wider uppercase">
                        {item.category}
                      </span>
                    </div>

                    {/* Details */}
                    <div className="p-5 space-y-2">
                      <div className="flex items-start justify-between gap-3">
                        <h4 className="font-extrabold text-sm sm:text-base text-slate-900 leading-tight">
                          {item.name}
                        </h4>
                        <span className="text-sm font-extrabold text-asmm-red whitespace-nowrap shrink-0">
                          {item.id === 'merch-baju-silat' || item.name.toLowerCase().includes('baju silat')
                            ? 'RM 120.00 – RM 140.00'
                            : `RM ${item.price.toFixed(2)}`}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 font-medium leading-relaxed line-clamp-3">
                        {item.description}
                      </p>
                    </div>
                  </div>

                  {/* Footer Action */}
                  <div className="px-5 pb-5 pt-1 flex items-center justify-between gap-3 border-t border-slate-55 mt-auto">
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${item.stockStatus === 'Dalam Stok' ? 'bg-emerald-500' : item.stockStatus === 'Stok Rendah' ? 'bg-amber-500' : 'bg-rose-500'
                        }`} />
                      <span className="text-[10px] font-bold text-slate-500">{item.stockStatus}</span>
                    </div>

                    <span className="text-xs text-slate-400 group-hover:text-asmm-red font-extrabold flex items-center gap-1 transition-all">
                      <span>Lihat Butiran</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-3xl p-10 text-center space-y-2">
              <Package className="w-12 h-12 text-slate-300 mx-auto" />
              <h4 className="font-extrabold text-sm text-slate-700">Tiada Barangan Dijumpai</h4>
              <p className="text-xs text-slate-400 font-medium">Cuba ubah kata kunci carian atau penapis kategori anda.</p>
            </div>
          )}
        </div>
      )}

      {/* Instant DuitNow QR Payment Modal */}
      <DuitNowQrModal
        isOpen={Boolean(placedOrderForQr)}
        onClose={handleQrModalClose}
        order={placedOrderForQr}
        onPaymentSubmitted={handlePaymentSubmitted}
      />

      {/* Uniform Size Chart Modal (Same as Registration) */}
      {isSizeChartOpen && (
        <UniformSizeChartModal
          isOpen={isSizeChartOpen}
          onClose={() => setIsSizeChartOpen(false)}
          selectedSize={selectedSize}
          onSelectSize={(size) => {
            setSelectedSize(size);
            setIsSizeChartOpen(false);
          }}
        />
      )}
    </div>
  );
};
